import { useOnboardingStore } from './store';

/**
 * Mapping Peruri resultCode → pesan user-friendly (ID + EN).
 * Sumber kode error: dokumen v3.11 halaman error code (md/Dokumen Web Service
 * Digital Signature v3.11.md) + observasi hasil probe 2026-04-21.
 *
 * Struktur key: `<endpoint>.<code>`
 * Fallback: pakai resultDesc asli dari Peruri.
 */

type ErrorDict = Record<string, { id: string; en: string }>;

/** API CHECK CERTIFICATE */
const CHECK_CERT: ErrorDict = {
    '0': {
        id: 'Sertifikat Anda aktif dan siap digunakan.',
        en: 'Your certificate is active and ready to use.',
    },
    '01': { id: 'SystemID tidak valid.', en: 'Invalid SystemID.' },
    '02': {
        id: 'Email belum terdaftar. Silakan mulai registrasi.',
        en: 'Email not registered. Please start registration.',
    },
    '03': {
        id: 'Percobaan verifikasi e-KYC sudah mencapai batas maksimum (3 kali). Coba lagi setelah 24 jam.',
        en: 'E-KYC verification limit reached (3 times). Try again after 24 hours.',
    },
    '04': {
        id: 'Anda belum melakukan verifikasi e-KYC. Silakan lanjutkan proses liveness.',
        en: 'You have not completed e-KYC verification. Please continue the liveness step.',
    },
    '05': {
        id: 'Akun belum diaktivasi. Cek email untuk link aktivasi.',
        en: 'Account not yet activated. Please check your email for the activation link.',
    },
    '06': {
        id: 'Sertifikat sudah dicabut dan belum dipublikasikan pada CRL.',
        en: 'Certificate revoked and not yet published to CRL.',
    },
    '07': {
        id: 'Masa berlaku sertifikat sudah habis. Silakan lakukan renewal.',
        en: 'Certificate has expired. Please renew.',
    },
    '08': {
        id: 'Sertifikat sedang diproses oleh Peruri. Silakan tunggu sebentar.',
        en: 'Certificate is being processed by Peruri. Please wait.',
    },
    '09': {
        id: 'Sertifikat sudah dicabut dan dipublikasikan pada CRL.',
        en: 'Certificate has been revoked and published to CRL.',
    },
    '10': {
        id: 'Email belum diverifikasi. Silakan cek inbox Anda.',
        en: 'Email not verified. Please check your inbox.',
    },
    '11': {
        id: 'Sertifikat gagal dibuat. Hubungi dukungan.',
        en: 'Certificate creation failed. Please contact support.',
    },
    '14': {
        id: 'Anda belum memverifikasi nomor HP dan e-KYC.',
        en: 'You have not verified your phone and completed e-KYC.',
    },
    '15': {
        id: 'Proses e-KYC sedang menunggu verifikasi manual oleh tim Peruri.',
        en: 'E-KYC process awaiting manual review by Peruri team.',
    },
    '16': {
        id: 'Verifikasi ditolak. Silakan ulangi proses onboarding dengan data yang benar.',
        en: 'Verification rejected. Please restart onboarding with correct data.',
    },
    '99': {
        id: 'Terjadi kesalahan umum. Silakan coba lagi.',
        en: 'General error occurred. Please try again.',
    },
};

/** API VIDEO VERIFICATION FOR RENEWAL / E-KYC */
const VIDEO_VERIF: ErrorDict = {
    '01': {
        id: 'User tidak dapat melakukan proses ini (SystemID salah atau sertifikat belum expired).',
        en: 'User cannot perform this process (wrong SystemID or certificate not yet expired).',
    },
    '02': {
        id: 'User tidak ditemukan di sistem Peruri.',
        en: 'User not found in Peruri system.',
    },
    '03': { id: 'Gagal upload video.', en: 'Video upload failed.' },
    '04': {
        id: 'File yang diupload bukan video.',
        en: 'Uploaded file is not a video.',
    },
    '05': { id: 'URL foto KTP tidak ditemukan.', en: 'KTP photo URL not found.' },
    '06': { id: 'Foto KTP tidak ditemukan.', en: 'KTP photo not found.' },
    '07': {
        id: 'Gagal memproses foto KTP.',
        en: 'Failed to process KTP photo.',
    },
    '08': {
        id: 'Verifikasi wajah gagal. Silakan ulangi.',
        en: 'Face verification failed. Please try again.',
    },
    '09': {
        id: 'Gagal memproses renewal sertifikat.',
        en: 'Failed to process certificate renewal.',
    },
    '10': {
        id: 'Batas maksimum verifikasi tercapai (3x). Tunggu 24 jam untuk coba lagi.',
        en: 'Max verification attempts reached (3x). Wait 24 hours to retry.',
    },
    '1007': {
        id: 'Email belum terdaftar di Peruri.',
        en: 'Email not registered in Peruri.',
    },
};

/** Registration + generic */
const REGISTRATION: ErrorDict = {
    '33': {
        id: 'Format data tidak valid. Periksa kembali isian Anda.',
        en: 'Invalid payload format. Please review your input.',
    },
    '1002': {
        id: 'Email ini sudah pernah didaftarkan di Peruri. Silakan login dengan email tersebut, atau gunakan email berbeda.',
        en: 'This email is already registered at Peruri. Please log in with that email, or use a different one.',
    },
    '1003': {
        id: 'NIK ini sudah terdaftar di Peruri. Satu NIK hanya bisa dipakai untuk satu akun — silakan login dengan data tersebut.',
        en: 'This NIK is already registered at Peruri. Each NIK can only be used for one account — please log in with that data.',
    },
    '20073': {
        id: 'Email tidak diizinkan untuk registrasi.',
        en: 'Email not allowed for registration.',
    },
    '20077': { id: 'Email tidak terdaftar.', en: 'Email not registered.' },
};

type Endpoint = 'checkCertificate' | 'videoVerification' | 'registration' | 'generic';

const ENDPOINT_DICTS: Record<Endpoint, ErrorDict> = {
    checkCertificate: CHECK_CERT,
    videoVerification: VIDEO_VERIF,
    registration: REGISTRATION,
    generic: REGISTRATION,
};

/**
 * Translate resultCode + fallback resultDesc ke pesan UI dalam bahasa aktif.
 * Kalau code tidak ada di dict, fallback ke resultDesc asli.
 */
export function translatePeruriError(
    endpoint: Endpoint,
    resultCode: string,
    fallbackDesc: string | undefined,
    lang: 'id' | 'en'
): string {
    const dict = ENDPOINT_DICTS[endpoint];
    const entry = dict[resultCode];
    if (entry) return entry[lang];
    return fallbackDesc ?? 'Terjadi kesalahan';
}

/**
 * Hook-friendly helper: ambil lang dari store + translate.
 */
export function usePeruriErrorTranslator() {
    const lang = useOnboardingStore((s) => s.lang);
    return (endpoint: Endpoint, code: string, fallback?: string) =>
        translatePeruriError(endpoint, code, fallback, lang);
}
